import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "./RoomManagementDetails.css";
import { roomMasterActions } from "../../core/actions";
import CustomTooltip from "../common/CustomTooltip";
import { Loading } from "../erp-billing/Loading";
import { Box, InputAdornment, MenuItem, TextField } from "@mui/material";
import {
  ROOM_STATUS_AVAILABLE,
  ROOM_STATUS_BOOKED,
} from "../../shared/constants";

const ROOM_STATUS_ALL = "all";
const ROOM_STATUS_LABELS = {
  [ROOM_STATUS_AVAILABLE]: "Available",
  [ROOM_STATUS_BOOKED]: "Booked",
};

const RoomStatusBadge = (params) => (
  <span className={`room-status room-status--${params.value}`}>
    {ROOM_STATUS_LABELS[params.value] || params.value}
  </span>
);

const RoomManagementDetails = () => {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(ROOM_STATUS_AVAILABLE);

  useEffect(() => {
    dispatch(
      roomMasterActions.roomManagementDetails((resCb) => {
        if (resCb?.roomList) {
          const transformed = transformRoomData(resCb.roomList);
          setRowData(transformed);
        }
        setLoading(false);
      }),
    );
  }, [dispatch]);

  const transformRoomData = (rooms) =>
    rooms.map((element) => ({
      id: element.id,
      roomCode: element.roomCode,
      roomName: element.roomName,
      status:
        element.roomStatus?.bookstatus === ROOM_STATUS_BOOKED
          ? ROOM_STATUS_BOOKED
          : element.emergencyroomstatus?.bookstatus === ROOM_STATUS_BOOKED
            ? ROOM_STATUS_BOOKED
            : ROOM_STATUS_AVAILABLE,
      patientId: element.patient?.id || "-",
      patientName: element.patient?.fname || "-",
      patientPhone: element.patient?.phno || "-",
      floorName: element?.floorName || "-",
    }));

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const availableBeds = rowData.filter(
    (row) => row.status === ROOM_STATUS_AVAILABLE,
  ).length;
  const bookedBeds = rowData.filter(
    (row) => row.status === ROOM_STATUS_BOOKED,
  ).length;

  const filteredData = rowData.filter((row) => {
    const matchesStatus =
      statusFilter === ROOM_STATUS_ALL || row.status === statusFilter;
    const matchesSearch =
      row.roomCode?.toLowerCase().includes(searchTerm) ||
      row.roomName?.toLowerCase().includes(searchTerm) ||
      row.patientName?.toLowerCase().includes(searchTerm) ||
      row.patientPhone?.toLowerCase().includes(searchTerm) ||
      row.floorName?.toLowerCase().includes(searchTerm) ||
      row.status?.toLowerCase().includes(searchTerm) ||
      String(row.patientId).toLowerCase().includes(searchTerm);

    return matchesStatus && matchesSearch;
  });

  // Use flex for responsive columns
  const columnDefs = [
    {
      headerName: "Sl No",
      valueGetter: (params) => params.node.rowIndex + 1,
      maxWidth: 85,
    },
    { headerName: "Room Number", field: "roomCode", flex: 1.2 },
    { headerName: "Room Name", field: "roomName", flex: 2 },
    { headerName: "Floor Name", field: "floorName", flex: 1.5 },
    {
      headerName: "Status",
      field: "status",
      flex: 1.1,
      cellRendererFramework: RoomStatusBadge,
    },
    { headerName: "MAIN", field: "patientId", flex: 1 },
    { headerName: "Patient Name", field: "patientName", flex: 2 },
    { headerName: "Patient Phone", field: "patientPhone", flex: 2 },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    floatingFilter: true,
    resizable: true,
    headerClass: "room-availability-column-header",
    cellStyle: { textAlign: "left" },
  };

  if (loading) {
    return (
      <div className="room-availability-loading">
        <Loading isBackgroundTransparent />
      </div>
    );
  }

  return (
    <div className="room-availability-page">
      <div className="room-availability-heading">
        <div>
          <h4 className="page-title">Room Availability</h4>
        </div>

        <div className="room-availability-summary">
          <button
            type="button"
            className={`room-summary-card room-summary-card--available ${
              statusFilter === ROOM_STATUS_AVAILABLE ? "is-active" : ""
            }`}
            onClick={() => setStatusFilter(ROOM_STATUS_AVAILABLE)}
          >
            <span className="room-summary-card__icon material-icons">
              meeting_room
            </span>
            <span className="room-summary-card__content">
              <small>Available Beds</small>
              <strong>{availableBeds}</strong>
            </span>
          </button>
          <button
            type="button"
            className={`room-summary-card room-summary-card--booked ${
              statusFilter === ROOM_STATUS_BOOKED ? "is-active" : ""
            }`}
            onClick={() => setStatusFilter(ROOM_STATUS_BOOKED)}
          >
            <span className="room-summary-card__icon material-icons">hotel</span>
            <span className="room-summary-card__content">
              <small>Booked Beds</small>
              <strong>{bookedBeds}</strong>
            </span>
          </button>
          <button
            type="button"
            className={`room-summary-card room-summary-card--total ${
              statusFilter === ROOM_STATUS_ALL ? "is-active" : ""
            }`}
            onClick={() => setStatusFilter(ROOM_STATUS_ALL)}
          >
            <span className="room-summary-card__icon material-icons">
              apartment
            </span>
            <span className="room-summary-card__content">
              <small>Total Beds</small>
              <strong>{rowData.length}</strong>
            </span>
          </button>
        </div>
      </div>

      <Box className="room-availability-controls">
        <form className="main-navbar__search room-availability-search">
          <TextField
            id="global-search"
            fullWidth
            size="small"
            placeholder="Search room, floor, patient, MAIN or phone number"
            type="search"
            value={searchTerm}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <span className="material-icons room-search-icon">search</span>
                </InputAdornment>
              ),
            }}
          />
        </form>

        <TextField
          select
          size="small"
          label="Room Status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="room-status-filter"
        >
          <MenuItem value={ROOM_STATUS_AVAILABLE}>Available</MenuItem>
          <MenuItem value={ROOM_STATUS_BOOKED}>Booked</MenuItem>
          <MenuItem value={ROOM_STATUS_ALL}>All</MenuItem>
        </TextField>
      </Box>

      <div className="room-availability-table-section">
        <div className="room-availability-table-heading">
          <span>Rooms</span>
          <small>
            Showing {filteredData.length} of {rowData.length}
          </small>
        </div>
        <div className="room-availability-table-card">
          <div
            className="ag-theme-balham room-availability-grid"
            style={{ height: "59vh", width: `100%` }}
          >
            <AgGridReact
              frameworkComponents={{ CustomTooltip }}
              pagination={true}
              paginationPageSize={10}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              domLayout="normal"
              overlayNoRowsTemplate="No rooms match the selected filters"
              onGridReady={(params) => {
                params.api.sizeColumnsToFit();
              }}
              onFirstDataRendered={(params) => {
                params.api.sizeColumnsToFit();
              }}
              onGridSizeChanged={(params) => {
                params.api.sizeColumnsToFit();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomManagementDetails;
